import { useCallback, useEffect, useRef, useState } from "react";
import { useAssignedClass } from "../../hooks/use-assigned-class.ts";
import { openClassPicker } from "../../hooks/use-class-picker.ts";
import { useClasses } from "../../hooks/use-classes.ts";
import { useCurrentLoadout } from "../../hooks/use-current-loadout.ts";
import { useItems } from "../../hooks/use-items.ts";
import { useOwnedItems } from "../../hooks/use-owned-items.ts";
import { useOwnedWarbonds } from "../../hooks/use-owned-warbonds.ts";
import { isSlugValidForClass } from "../dossier/loadout/loadout-slot-match.ts";
import { LoadoutSlot } from "./loadout-slot.tsx";
import { LoadoutSlotPickerDialog } from "./loadout-slot-picker-dialog.tsx";
import { SLOT_DEFS, type SlotDef } from "./slot-labels.ts";

const COLLAPSE_KEY = "hd2:currentLoadoutCollapsed";

function readCollapsed(): boolean {
	try {
		return localStorage.getItem(COLLAPSE_KEY) === "true";
	} catch {
		return true;
	}
}

export function CurrentLoadoutWidget() {
	const [collapsed, setCollapsed] = useState(() => readCollapsed());
	const rootRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const el = rootRef.current;
		if (!el) {
			return;
		}
		const apply = () => {
			const h = Math.ceil(el.getBoundingClientRect().height);
			document.documentElement.style.setProperty(
				"--current-loadout-h",
				`${h}px`,
			);
		};
		apply();
		const ro = new ResizeObserver(apply);
		ro.observe(el);
		return () => {
			ro.disconnect();
			document.documentElement.style.removeProperty("--current-loadout-h");
		};
	}, []);
	const { loadout, equip, clear, clearAll } = useCurrentLoadout();
	const items = useItems();
	const ownedItems = useOwnedItems();
	const ownedWarbonds = useOwnedWarbonds();
	const [assigned] = useAssignedClass();
	const { classes } = useClasses();
	const [pickerTarget, setPickerTarget] = useState<SlotDef | null>(null);

	const assignedClass = assigned
		? classes.find((c) => c.slug === assigned.slug)
		: null;
	const themeColorHex = assignedClass?.themeColorHex;

	const toggleCollapsed = useCallback(() => {
		setCollapsed((prev) => {
			const next = !prev;
			localStorage.setItem(COLLAPSE_KEY, String(next));
			return next;
		});
	}, []);

	function getSlugForDef(def: SlotDef): string | null {
		if (def.slotKey === "stratagems" && def.stratagemIndex !== undefined) {
			return loadout.stratagems[def.stratagemIndex];
		}
		return loadout[def.slotKey] as string | null;
	}

	const filledCount = SLOT_DEFS.filter((d) => getSlugForDef(d) !== null).length;

	function isSlotInvalid(def: SlotDef): boolean {
		if (!assignedClass) {
			return false;
		}
		const slug = getSlugForDef(def);
		if (!slug) {
			return false;
		}
		return !isSlugValidForClass(def.slotKey, slug, assignedClass, items);
	}

	const invalidDefs = SLOT_DEFS.filter(isSlotInvalid);

	function clearInvalid() {
		for (const def of invalidDefs) {
			clear(def.slotKey, def.stratagemIndex);
		}
	}

	return (
		<div
			ref={rootRef}
			className="fixed right-0 bottom-0 left-0 z-40 border-t-4 border-primary bg-surface-container-lowest"
		>
			<div className="flex items-center justify-between px-4 py-2 md:px-6">
				<button
					type="button"
					className="flex cursor-pointer items-center gap-3"
					onClick={toggleCollapsed}
				>
					<span className="font-mono text-[10px] text-outline uppercase tracking-[0.2em]">
						{"// CURRENT LOADOUT"}
					</span>
					<span className="font-mono text-[9px] text-outline-variant">
						{`${filledCount}/${SLOT_DEFS.length}`}
					</span>
				</button>
				<div className="flex items-center gap-2 md:gap-3">
					{assignedClass ? (
						<button
							type="button"
							onClick={openClassPicker}
							className="cursor-pointer border-l-4 bg-surface-container px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-on-surface transition-colors hover:bg-surface-container-high"
							style={{ borderLeftColor: assignedClass.themeColorHex }}
						>
							{`// ${assignedClass.name.toUpperCase()}`}
						</button>
					) : (
						<button
							type="button"
							onClick={openClassPicker}
							className="cursor-pointer border border-primary bg-primary/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-primary transition-colors hover:bg-primary/20"
						>
							{"// NO CLASS — CHOOSE"}
						</button>
					)}
					{invalidDefs.length > 0 && (
						<button
							type="button"
							onClick={clearInvalid}
							className="cursor-pointer border border-error bg-error/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-error transition-colors hover:bg-error/20"
						>
							{`CLEAR ${invalidDefs.length} INVALID`}
						</button>
					)}
					{!collapsed && filledCount > 0 && (
						<button
							type="button"
							onClick={clearAll}
							className="cursor-pointer border border-outline px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-outline transition-colors hover:border-error hover:text-error"
						>
							{"CLEAR"}
						</button>
					)}
					<button
						type="button"
						onClick={toggleCollapsed}
						className="inline-grid h-7 w-7 cursor-pointer place-items-center"
					>
						<span className="material-symbols-outlined text-outline text-[18px] leading-none">
							{collapsed ? "expand_less" : "expand_more"}
						</span>
					</button>
				</div>
			</div>

			{!collapsed && (
				<div className="flex flex-wrap justify-center gap-1.5 px-4 pb-3 md:flex-nowrap md:justify-start md:gap-2 md:px-6">
					{SLOT_DEFS.map((def) => (
						<LoadoutSlot
							key={def.label}
							label={def.label}
							equippedSlug={getSlugForDef(def)}
							items={items}
							themeColorHex={themeColorHex}
							invalid={isSlotInvalid(def)}
							hoverable={
								def.slotKey === "primary" ||
								def.slotKey === "secondary" ||
								def.slotKey === "grenade" ||
								def.slotKey === "armor"
							}
							onClick={() => setPickerTarget(def)}
						/>
					))}
				</div>
			)}

			<LoadoutSlotPickerDialog
				open={pickerTarget !== null}
				slotLabel={pickerTarget?.label ?? ""}
				slotKey={pickerTarget?.slotKey ?? "primary"}
				items={items}
				ownedItemSlugs={ownedItems.owned}
				excludedItemSlugs={ownedItems.excluded}
				ownedWarbondSlugs={ownedWarbonds.owned}
				onSelect={(slug) => {
					if (pickerTarget) {
						equip(pickerTarget.slotKey, slug, pickerTarget.stratagemIndex);
					}
				}}
				onClear={() => {
					if (pickerTarget) {
						clear(pickerTarget.slotKey, pickerTarget.stratagemIndex);
					}
				}}
				onClose={() => setPickerTarget(null)}
			/>
		</div>
	);
}
